import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './styles.module.css';

import xamaLogoAzul from '../../assets/logos/xama-logo-azul.png';
import {
  LinkedinService,
  MetaService,
  TiktokService,
  TwitterService,
} from '../../services';
import { useRedirect } from '../../hooks/useRedirectPAge';
import { useClient } from '../../hooks/useClient';
import { postMetaByBusiness } from '../../services/metaService';
import {
  clearInstagramOAuthPending,
  INSTAGRAM_POPUP_CLOSE_DELAY_MS,
  isInstagramPopupOnlyReturnPage,
  OAUTH_COMPLETED_MESSAGE_TYPE,
  OAUTH_RESULT_STORAGE_KEY,
  readInstagramOAuthPending,
  resolveInstagramOAuthReturnPath,
  writeInstagramOAuthReturnResult,
} from '../../shared/campaign/utils/instagramOAuth';

const INSTAGRAM_REDIRECT_DELAY_MS = 1200;

function SocialMediaRedirect() {
  const { redirectUrl, idParam, setRedirect } = useRedirect();
  const navigation = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const queryBusinessId = queryParams.get('businessId');
  const queryCode = queryParams.get('code');
  const queryMedia = queryParams.get('media');
  const queryOauthToken = queryParams.get('oauth_token');
  const queryOauthVerifier = queryParams.get('oauth_verifier');
  const { setClientData } = useClient();

  const [instagramLogin, setInstagramLogin] = useState(undefined);

  const notifyOAuthCompleted = (provider, success = false) => {
    const payload = {
      type: OAUTH_COMPLETED_MESSAGE_TYPE,
      provider,
      success: !!success,
    };

    try {
      localStorage.setItem(
        OAUTH_RESULT_STORAGE_KEY,
        JSON.stringify({
          provider,
          success: !!success,
          ts: Date.now(),
        }),
      );
    } catch (error) {
      console.error('Erro ao persistir resultado do OAuth:', error);
    }

    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(payload, window.location.origin);
      }
    } catch (error) {
      console.error('Erro ao notificar janela principal do OAuth:', error);
    }
  };

  const closeOAuthPopup = () => {
    try {
      window.close();
    } catch {
      // noop
    }
  };

  const finishInstagramOAuth = useCallback(
    ({ success, navigationSelected, businessId }) => {
      const pending = readInstagramOAuthPending();
      const returnPath =
        pending?.returnPath ||
        resolveInstagramOAuthReturnPath(navigationSelected);

      writeInstagramOAuthReturnResult({
        success,
        returnPath,
        businessId,
      });
      clearInstagramOAuthPending();

      setTimeout(() => {
        navigation(returnPath, {
          replace: true,
          state: {
            instagramOAuth: {
              success,
              returnPath,
              businessId,
            },
          },
        });
      }, INSTAGRAM_REDIRECT_DELAY_MS);
    },
    [navigation],
  );

  const handleInstagramOAuthResult = useCallback(
    ({ success, navigationSelected, businessId }) => {
      const isPopup = !!(window.opener && !window.opener.closed);

      if (isPopup && isInstagramPopupOnlyReturnPage(navigationSelected)) {
        notifyOAuthCompleted('instagram', success);
        clearInstagramOAuthPending();

        setTimeout(() => {
          closeOAuthPopup();
        }, INSTAGRAM_POPUP_CLOSE_DELAY_MS);
        return;
      }

      finishInstagramOAuth({
        success,
        navigationSelected,
        businessId,
      });
    },
    [finishInstagramOAuth],
  );

  const executeMetaLogin = async () => {
    const verifyReturnState = queryParams.get('state');
    const redirectUri = `${window.location.origin}/SocialMediaRedirect`;
    const navigationSelected = verifyReturnState.split(',')[1];
    const businessId = verifyReturnState.split(',')[2];

    const body = {
      businessId,
      code: queryCode,
      redirectUri,
    };

    const metaByBusinessResponse = await postMetaByBusiness(body);

    if (metaByBusinessResponse?.dataL?.conected) {
      setInstagramLogin('conected');
      handleInstagramOAuthResult({
        success: true,
        navigationSelected,
        businessId,
      });
      return;
    }

    setInstagramLogin('error');
    handleInstagramOAuthResult({
      success: false,
      navigationSelected,
      businessId,
    });
  };

  const handleInstagramOAuthDenied = (verifyReturnState) => {
    const navigationSelected = verifyReturnState.split(',')[1];
    const businessId = verifyReturnState.split(',')[2];

    setInstagramLogin('error');
    handleInstagramOAuthResult({
      success: false,
      navigationSelected,
      businessId,
    });
  };

  useEffect(() => {
    if (window.location.hash === '#_' || window.location.hash.startsWith('#_=')) {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
    }
  }, []);

  useEffect(() => {
    const verifyReturnState = queryParams.get('state');

    if (verifyReturnState?.includes('instagram')) {
      if (queryParams.get('error')) {
        handleInstagramOAuthDenied(verifyReturnState);
        return;
      }

      setInstagramLogin('generating_short_token');
      executeMetaLogin();
      return;
    }

    if (verifyReturnState?.includes('media=3')) {
      tiktokCallback();
      return;
    }

    if (verifyReturnState?.includes('media=5')) {
      facebookCallback();
      return;
    }

    switch (queryMedia) {
    case '1':
      linkedinCallback();
      break;
    case '2':
      twitterV1Callback();
      break;
    case '2a':
      twitterCallback();
      break;

    default:
      setTimeout(() => {
        navigation('/StepsPage', {
          state: {
            queryCode: queryCode,
          },
        });
      }, 5000);

      break;
    }
  }, []);

  const setClient = (idParam) => {
    setClientData(idParam);
    setRedirect(null, null);
  };

  const linkedinCallback = async () => {
    const { responseStatus } = await LinkedinService.getLinkedinCallback(
      queryBusinessId,
      queryCode,
    );

    if (responseStatus === 200 || responseStatus === 204) {
      const timeoutId = setTimeout(() => {
        navigation(redirectUrl, {
          state: {
            queryBusinessId: queryBusinessId,
            queryCode: queryCode,
          },
        });
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  };

  const twitterV1Callback = async () => {
    const { responseStatus } = await TwitterService.getTwitterV1Callback(
      queryBusinessId,
      queryOauthToken,
      queryOauthVerifier,
    );

    if (responseStatus === 200 || responseStatus === 204) {
      setClient(idParam);

      const timeoutId = setTimeout(() => {
        navigation(redirectUrl, {
          state: {
            redirect: true,
            queryBusinessId: queryBusinessId,
            queryCode: queryCode,
          },
        });
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  };

  const twitterCallback = async () => {
    const { responseStatus } = await TwitterService.getTwitterCallback(
      queryBusinessId,
      queryCode,
    );

    if (responseStatus === 200 || responseStatus === 204) {
      const timeoutId = setTimeout(() => {
        navigation('/StepsPage', {
          state: {
            queryBusinessId: queryBusinessId,
            queryCode: queryCode,
          },
        });
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  };

  const facebookCallback = async () => {
    const queryState = queryParams.get('state');
    const parts = queryState.split(',');
    const businessIdPart = parts.find((part) => part.includes('businessId='));
    const businessIdValue = businessIdPart.split('=')[1];

    const { responseStatus } = await MetaService.getFacebookcallback(
      businessIdValue,
      queryCode,
    );

    if (responseStatus === 200 || responseStatus === 204) {
      const timeoutId = setTimeout(() => {
        navigation(redirectUrl, {
          state: {
            queryBusinessId: queryBusinessId,
            queryCode: queryCode,
          },
        });
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  };

  const tiktokCallback = async () => {
    const queryState = queryParams.get('state');
    const parts = queryState.split(',');
    const businessIdPart = parts.find((part) => part.includes('businessId='));
    const businessIdValue = businessIdPart.split('=')[1];

    const { responseStatus } = await TiktokService.getTiktokcallback(
      businessIdValue,
      queryCode,
      queryState,
    );

    if (responseStatus === 200 || responseStatus === 204) {
      const timeoutId = setTimeout(() => {
        navigation(redirectUrl, {
          state: {
            queryBusinessId: queryBusinessId,
            queryCode: queryCode,
          },
        });
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.logo}>
        <img
          src={xamaLogoAzul}
          alt="posteiLogoText"
          style={{ height: 34 }}
        />
      </div>

      <div className={styles.divider} />
      <div className={styles.container}>
        {!instagramLogin && <h1>Login efetuado com sucesso!</h1>}
        {instagramLogin === 'generating_short_token' && (
          <h1>Efetuando Login no Instagram</h1>
        )}
        {instagramLogin === 'conected' && (
          <h1>Instagram conectado com sucesso!</h1>
        )}
        {instagramLogin === 'error' && (
          <h1>Erro ao conectar com o Instagram!</h1>
        )}
      </div>
    </div>
  );
}

export default SocialMediaRedirect;
